import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import Layout from "../components/Layout";
import { Plus, Trash2, Copy, Calendar, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Calendar as CalendarPicker } from "../components/ui/calendar";
import { fmtDateMed } from "../lib/dates";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { toast } from "sonner";
import { format } from "date-fns";

const Templates = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyDialogOpen, setApplyDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [applying, setApplying] = useState(false);
  const [applyForm, setApplyForm] = useState({
    roster_name: "",
    start_date: null
  });

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/templates`, { withCredentials: true });
      setTemplates(response.data);
    } catch (error) {
      console.error("Failed to fetch templates:", error);
      toast.error("Failed to load templates");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm("Delete this template?")) return;
    try {
      await axios.delete(`${API}/templates/${templateId}`, { withCredentials: true });
      toast.success("Template deleted");
      fetchTemplates();
    } catch (error) {
      console.error("Failed to delete template:", error);
      toast.error("Failed to delete template");
    }
  };

  const openApplyDialog = (template) => {
    setSelectedTemplate(template);
    setApplyForm({
      roster_name: `${template.name} — New`,
      start_date: null
    });
    setApplyDialogOpen(true);
  };

  const handleApply = async (e) => {
    e.preventDefault();
    if (!applyForm.start_date || !applyForm.roster_name) {
      toast.error("Please fill in all fields");
      return;
    }

    setApplying(true);
    try {
      const response = await axios.post(`${API}/templates/apply`, {
        template_id: selectedTemplate.template_id,
        roster_name: applyForm.roster_name,
        start_date: format(applyForm.start_date, "yyyy-MM-dd")
      }, { withCredentials: true });
      toast.success("Roster created from template!");
      setApplyDialogOpen(false);
      navigate(`/rosters/${response.data.roster_id}`);
    } catch (error) {
      console.error("Failed to apply template:", error);
      toast.error("Failed to create roster from template");
    } finally {
      setApplying(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="templates-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl tracking-tight font-bold text-zinc-900 dark:text-zinc-100">Roster Templates</h1>
          <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
            Save and reuse roster patterns for recurring schedules
          </p>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-100 p-4">
          <p className="text-sm text-blue-800">
            <strong>How to create a template:</strong> Open any roster with assignments and click 
            "Save as Template" to capture the shift pattern. Then use it here to create new rosters instantly.
          </p>
        </div>

        {/* Templates List */}
        {templates.length === 0 ? (
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 p-12 text-center">
            <div className="w-12 h-12 bg-zinc-100 dark:bg-zinc-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <Copy className="w-6 h-6 text-zinc-400 dark:text-zinc-500" />
            </div>
            <h3 className="text-sm font-medium text-zinc-900 dark:text-zinc-100 mb-1">No templates yet</h3>
            <p className="text-xs text-zinc-500 dark:text-zinc-400 mb-4">
              Go to a roster and click "Save as Template" to create your first reusable pattern
            </p>
            <Button
              onClick={() => navigate("/rosters")}
              variant="outline"
              data-testid="go-to-rosters-btn"
            >
              Go to Rosters
            </Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200 dark:border-zinc-800">
            {templates.map((template) => (
              <div key={template.template_id} className="bg-white dark:bg-zinc-900 p-6 flex flex-col" data-testid={`template-${template.template_id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-base font-medium text-zinc-900 dark:text-zinc-100">{template.name}</h3>
                    {template.description && (
                      <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{template.description}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(template.template_id)}
                    className="text-zinc-400 dark:text-zinc-500 hover:text-red-600"
                    data-testid={`delete-template-${template.template_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-2 flex-1 mb-4">
                  <div className="flex items-center justify-between py-2 border-b border-zinc-100 dark:border-zinc-800">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Duration</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100">
                      {template.duration_days} days
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2">
                    <span className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Entries</span>
                    <span className="text-sm tabular-nums font-medium text-zinc-900 dark:text-zinc-100">
                      {template.shift_pattern?.length || 0}
                    </span>
                  </div>
                </div>
                
                <Button
                  className="w-full bg-[#002FA7] hover:bg-[#00227A] text-white"
                  onClick={() => openApplyDialog(template)}
                  data-testid={`apply-template-${template.template_id}`}
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Use Template
                </Button>
              </div>
            ))}
          </div>
        )}

        {/* Apply Template Dialog */}
        <Dialog open={applyDialogOpen} onOpenChange={setApplyDialogOpen}>
          <DialogContent className="sm:max-w-xl">
            <DialogHeader>
              <DialogTitle className="text-xl tracking-tight font-bold">
                Create Roster from Template
              </DialogTitle>
            </DialogHeader>
            {selectedTemplate && (
              <form onSubmit={handleApply} className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4 items-start">
                <div className="bg-zinc-50 dark:bg-zinc-800/50 border border-zinc-200 dark:border-zinc-800 p-3 rounded sm:col-span-2">
                  <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 dark:text-zinc-400">Template</p>
                  <p className="text-sm font-medium text-zinc-900 dark:text-zinc-100">{selectedTemplate.name}</p>
                  <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5">
                    {selectedTemplate.duration_days} days, {selectedTemplate.shift_pattern?.length || 0} entries
                  </p>
                </div>
                
                <div>
                  <Label htmlFor="roster_name" className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Roster Name *
                  </Label>
                  <Input
                    id="roster_name"
                    value={applyForm.roster_name}
                    onChange={(e) => setApplyForm({ ...applyForm, roster_name: e.target.value })}
                    required
                    className="mt-1"
                    data-testid="apply-roster-name"
                  />
                </div>

                <div>
                  <Label className="text-xs uppercase tracking-wide text-zinc-500 dark:text-zinc-400">
                    Start Date *
                  </Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className="w-full mt-1 justify-start text-left font-normal"
                        data-testid="apply-start-date"
                      >
                        {applyForm.start_date ? fmtDateMed(applyForm.start_date) : "Pick start date"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <CalendarPicker
                        mode="single"
                        selected={applyForm.start_date}
                        onSelect={(date) => setApplyForm({ ...applyForm, start_date: date })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex justify-end gap-3 pt-4 sm:col-span-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setApplyDialogOpen(false)}
                    data-testid="cancel-apply-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={applying}
                    className="bg-[#002FA7] hover:bg-[#00227A] text-white"
                    data-testid="create-from-template-btn"
                  >
                    {applying ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Calendar className="w-4 h-4 mr-2" />
                    )}
                    Create Roster
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default Templates;
